/**
 * ERP-ИЙН ХЭРЭГЛЭГЧИЙН ХҮРЭЭ — API (`ErpScopeService`) ба админы (`types/api/erp.ts`, ХН-ийн «Ажилтны тохиргоо» → «1С-ийн лавлахууд» таб) НЭГ толь.
 * Ү8 (docs/31 §4u, D8-1/D8-2/D8-8/D8-9).
 *
 * ! ХҮРЭЭ = МӨР, ТҮВШИН = ХЭМЖИГДЭХҮҮН. Борлуулалтын суваг нь БҮХ түвшинд мөрийн хэмжээс (агуулах, бүсийн адил
 * ! applied/dropped/scopeFallback); түвшин (`bi-tier.ts`) зөвхөн мөнгө харуулах эсэхийг шийднэ.
 *
 * ! «СУВАГ» ГЭЖ ЮУ ВЭ (D8-1): `Справочник.Партнеры._Fld24063RRef` (maxPrimarySalesChannel → `_Reference24061`
 * ! «Борлуулалтын ерөнхий суваг», 9 утга: RA, OS, KA …) — мөрийн ХАРИЛЦАГЧИЙН ОДООГИЙН утга. Хэрэглэгч дээр
 * ! `_Reference24061`-ийн uuid-аар (lower-case) хадгална — RA/OS нэр, `_Code` БИШ.
 * ! ⚠️ «КаналТТ» (`_Fld24005RRef` → `_Reference24004`, `customerChannels`, `outletType`) нь ӨӨР хэмжээс — хольж болохгүй.
 */

/** `ErpScope`-ийн хэмжээс (uuid жагсаалт). Шинэ хэмжээс = энд + API-ийн `KIND_FIELD` + хамрах хүснэгт (API-SCOPE-TIER). */
export const ERP_SCOPE_DIMENSIONS = [
  'warehouses',
  'businessRegions',
  'deliveryZones',
  'routes',
  'brands',
  'salesChannels'
] as const

export type ErpScopeDimension = (typeof ERP_SCOPE_DIMENSIONS)[number]

/**
 * `GET /erp/scope`-ийн хэлбэр (API `ErpScope`-той ИЖИЛ).
 *   `restricted` — `false` бол жагсаалтууд утгагүй (хэрэглэгч бүгдийг харна);
 *   `linked` (Ү8) — хэрэглэгч ХН-ийн ажилтантай холбогдсон эсэх (`no-employee` ба `no-channel`-ийг ялгана, D8-3).
 */
export type ErpScopeShape = { restricted: boolean; linked: boolean } & Record<ErpScopeDimension, string[]>

/** ХН-ийн лавлахын төрөл (`hr_employee_references.kind`) — борлуулалтын суваг. Bridge `GET /api/cloud/v1/customers/sales-channels`. */
export const SALES_CHANNEL_REFERENCE_KIND = 'sales_channel' as const

/**
 * МӨНГӨ НЭЭХ хүрээний лавлахын төрөл — бичилт нь `hr.employees:scope`-ийг НЭМЖ шаардана, ӨӨРТӨӨ хуваарилахыг хориглоно,
 * `erp.routes:manage`-ийн замаар (`/erp/routes/employees/:id/references`) ХЭЗЭЭ Ч бичигдэхгүй (D8-9).
 */
export const MONEY_SCOPE_REFERENCE_KINDS = [SALES_CHANNEL_REFERENCE_KIND] as const

export const HR_EMPLOYEE_SCOPE_PERMISSION = 'hr.employees:scope' as const

export const isMoneyScopeReferenceKind = (kind: string): boolean =>
  (MONEY_SCOPE_REFERENCE_KINDS as readonly string[]).includes(kind)

/**
 * ХУВААРИЛАЛТЫН БИЧИЛТИЙН ШАЛГАЛТ (цэвэр) — API (403/400) ба админ (товч нуух/тайлбар) ИЖИЛ дүрэм:
 *   `route-endpoint` — `erp.routes:manage`-ийн замаар мөнгөний хүрээ бичих оролдлого (400);
 *   `permission`     — `hr.employees:scope` алга (403);
 *   `self-scope`     — өөрийн ажилтанд (403) — хэн ч (super-admin ч) өөртөө суваг өгөхгүй.
 * Мөнгөний хүрээ БИШ төрөл (агуулах, бүс …) → `null` (одоогийн `hr.employees:edit` / `erp.routes:manage` хэвээр, design §10.3).
 */
export type ScopeReferenceWriteProblem = 'route-endpoint' | 'permission' | 'self-scope'

export const scopeReferenceWriteProblem = (input: {
  kind: string
  via: 'hr' | 'erp.routes'
  actorPermissions: readonly string[] | null | undefined
  actorEmployeeId: string | null | undefined
  targetEmployeeId: string
}): ScopeReferenceWriteProblem | null => {
  if (!isMoneyScopeReferenceKind(input.kind)) return null
  if (input.via === 'erp.routes') return 'route-endpoint'
  if (!input.actorPermissions?.includes(HR_EMPLOYEE_SCOPE_PERMISSION)) return 'permission'
  if (input.actorEmployeeId != null && String(input.actorEmployeeId) === String(input.targetEmployeeId)) return 'self-scope'

  return null
}

/**
 * Ш9 / D8-8 — ХҮРЭЭГ ХЭРЭГЖҮҮЛЖ ЧАДАХГҮЙ цэгийн 403: `{ statusCode: 403, error: 'Forbidden', reason: 'scope', detail, message }`
 * (`/erp/sales/stats`-ийн одоогийн хэлбэр). `detail` — ямар хэмжээс огтлогдох боломжгүй вэ. `salesChannel` (Ү8) — bridge-ийн
 * жагсаалт сувгийн параметргүй (`/erp/sales|returns|payments|orders|plans`, `/erp/sales/stats`); Ү8c-д bridge-ийн параметрээр нээнэ.
 */
export const ERP_SCOPE_FORBIDDEN_DETAILS = ['multiWarehouse', 'businessRegion', 'deliveryZone', 'salesChannel'] as const

export type ErpScopeForbiddenDetail = (typeof ERP_SCOPE_FORBIDDEN_DETAILS)[number]

export type ErpScopeForbiddenBody = {
  statusCode: 403
  error: 'Forbidden'
  reason: 'scope'
  detail: ErpScopeForbiddenDetail
  message: string
}

export const erpScopeForbiddenBody = (detail: ErpScopeForbiddenDetail, message: string): ErpScopeForbiddenBody => ({
  statusCode: 403,
  error: 'Forbidden',
  reason: 'scope',
  detail,
  message
})

/**
 * Ү8 (D8-10) — санхүүгийн BI-ийн any-of эрх (`@RequireAnyPermission`): жагсаалтын эрх ЭСВЭЛ нэгтгэлийн эрх. `sales-manager`
 * дүр зөвхөн нэгтгэлийн эрхтэй (жагсаалтын дэлгэц нээгдэхгүй).
 */
export const BI_FINANCE_CREDIT_PERMISSIONS = ['erp.credit:list', 'erp.credit:aggregate'] as const

export const BI_FINANCE_PAYMENTS_PERMISSIONS = ['erp.sales:list', 'erp.sales:aggregate'] as const
